import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, Transition as _Transition } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "ml-0.5"
}
const _hoisted_2 = { class: "mb-4 relative lg:max-w-[280px] lg:w-[280px]" }
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = { class: "bg-white rounded-md shadow m-0.5 overflow-y-auto h-96 lg:max-w-[280px] lg:w-[280px]" }
const _hoisted_5 = ["id"]
const _hoisted_6 = { class: "flex px-3 items-center justify-between sticky top-0 bg-white z-50" }
const _hoisted_7 = { class: "font-bold py-1" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = { class: "flex items-center" }
const _hoisted_10 = {
  key: 0,
  class: "h-4 w-4 rounded bg-yellow-600 text-white cursor-pointer text-center leading-4 text-sm"
}
const _hoisted_11 = ["checked"]
const _hoisted_12 = {
  key: 2,
  class: "absolute inset-y-0 right-0 flex items-center pr-4"
}
const _hoisted_13 = {
  key: 0,
  class: "text-gray-400 text-sm"
}
const _hoisted_14 = {
  for: "",
  class: "cursor-pointer block truncate text-sm ml-3"
}

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { XMarkIcon } from "@heroicons/vue/20/solid";
import { ChevronUpDownIcon } from "@heroicons/vue/24/outline";
import { computed, PropType, ref, Ref, watch } from "vue";
import { useI18n } from "vue-i18n";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProcessTypesFilter',
  props: {
  options: {
    type: Array as unknown as PropType<{ value: string; name: string }[]>,
  },
  groups: {
    type: Array as unknown as PropType<
      { name: string; field: string; options: { value: string; name: string }[] }[]
    >,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: [String, Array, Object] as PropType<
      string | string[] | Record<string, string | string[]>
    >,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  isGantt: {
    type: Boolean,
  },
  class: {
    type: String,
    default: "",
  },
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  dependencies: {
    type: Object as PropType<
      Record<
        string,
        {
          to: string;
          defineDependencies: (options: string[], value: string) => string[];
        }
      >
    >,
    default: () => ({}),
  },
},
  emits: ["update:selected", "userInput"],
  setup(__props, { emit: __emit }) {

const { t } = useI18n();

const props = __props;

const mapDefaultSelectedProps = (value: unknown) => {
  if (props.options) {
    return new Set(value as string | string[]);
  }
  if (props.groups) {
    return Object.fromEntries(
      props.groups.map((group) => {
        return [group.field, new Set((value as Record<string, string | Set<string>>)[group.field])];
      }),
    );
  }
};

const emits = __emit;

const selected = ref(mapDefaultSelectedProps(props.selected) || {}) as unknown as Ref<
  Record<string, Set<string>>
>;

watch(
  () => props.selected,
  (value) => {
    const defaultSelected = mapDefaultSelectedProps(value) || {};
    selected.value = defaultSelected as Record<string, Set<string>>;
  },
);

const query = ref("");

const selectedCount = computed(() => {
  if (!selected.value) return;

  const selectedItems = selected.value as unknown as Record<string, Set<string>>;
  return selectedItems.processes.size;
});

const countDependencies = (field: string, value: string) => {
  const dependencies = props.dependencies[field];
  if (!dependencies) return [0, 0];

  const { to, defineDependencies } = dependencies;
  const options =
    props.groups?.find((group) => group.field === to)?.options.map((option) => option.value) || [];
  const definedDependencies = defineDependencies(options, value);

  const selectedItemsCount =
    definedDependencies.filter((dependency) =>
      (selected.value as Record<string, Set<string>>)[to].has(dependency),
    ).length || 0;

  return [selectedItemsCount, definedDependencies.length];
};

const filteredGroups = computed(() => {
  if (!query.value || !props.groups) {
    return props.groups;
  }

  return props.groups.map((group) => ({
    ...group,
    options: group.options.filter((option) =>
      option.name.toLowerCase().includes(query.value.toLowerCase()),
    ),
  }));
});

const handleGroupsChange = (group: string, value: string) => {
  const selectedValue = selected.value as Record<string, Set<string>>;
  if (props.multiple) {
    const selectedInGroup = selectedValue[group];

    if (selectedInGroup.has(value)) {
      selectedInGroup.delete(value);
    } else {
      selectedInGroup.add(value);
      emits("userInput");
    }
  } else {
    selectedValue[group] = new Set(value);
  }

  const selects = Object.fromEntries(
    Object.entries(selectedValue).map(([key, value]) => {
      return [key, Array.from(value)];
    }),
  );
  emits("update:selected", selects);
};

const searchValue = ref("");

const resetSearchBar = () => {
  searchValue.value = "";
};

const clearSelected = () => {
  const clearedSelected = selected.value as Record<string, Set<string>>;
  for (const key in selected.value) {
    if (Object.prototype.hasOwnProperty.call(selected.value, key)) {
      clearedSelected[key] = new Set();
    }
  }
  const selects = Object.fromEntries(
    Object.entries(clearedSelected).map(([key, value]) => {
      return [key, Array.from(value)];
    }),
  );
  emits("update:selected", selects);
};
const popover = ref();
const searchProcess = ref();
watch(popover, (isOpen) => {
  if (isOpen) {
    searchProcess.value?.focus();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Popover), { class: "relative" }, {
    default: _withCtx(({ open }) => [
      (!__props.defaultOpen)
        ? (_openBlock(), _createBlock(_unref(PopoverButton), {
            key: 0,
            class: _normalizeClass([
        'lg:w-72 mt-2 lg:mt-0 group inline-flex items-center justify-between py-1.5 px-3 bg-white rounded-md text-left shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none sm:text-sm text-xs sm:leading-6 w-full text-gray-400',
        open ? 'ring-2 ring-yellow-500' : '',
      ])
          }, {
            default: _withCtx(() => [
              _createElementVNode("span", null, [
                _createTextVNode(_toDisplayString(_unref(t)(__props.placeholder)), 1),
                (selectedCount.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_1, "(" + _toDisplayString(selectedCount.value) + ")", 1))
                  : _createCommentVNode("", true)
              ]),
              _createVNode(_unref(ChevronUpDownIcon), {
                class: "ml-2 h-5 w-5 transition duration-150 ease-in-out",
                "aria-hidden": "true"
              })
            ]),
            _: 2
          }, 1032, ["class"]))
        : _createCommentVNode("", true),
      _createVNode(_Transition, {
        "enter-active-class": "transition duration-200 ease-out",
        "enter-from-class": "translate-y-1 opacity-0",
        "enter-to-class": "translate-y-0 opacity-100",
        "leave-active-class": "transition duration-150 ease-in",
        "leave-from-class": "translate-y-0 opacity-100",
        "leave-to-class": "translate-y-1 opacity-0"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PopoverPanel), {
            static: __props.defaultOpen,
            class: _normalizeClass([[
          'absolute z-10 min-w-max h-max w-full md:-translate-x-w-max transform px-4 sm:px-0 lg:max-w-5xl max-h-screen overflow-y-visible',
          __props.defaultOpen ? '' : 'mt-3',
        ], "PopoverPanelContent"])
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", {
                class: "bg-gray-50 border border-gray-200 shadow rounded-lg p-4",
                ref_key: "popover",
                ref: popover
              }, [
                _createElementVNode("div", _hoisted_2, [
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "searchProcess",
                    ref: searchProcess,
                    type: "text",
                    class: "w-full px-3 py-1 text-sm rounded-md border border-gray-300 focus:ring-yellow-500 focus:border-yellow-500",
                    placeholder: _unref(t)('buttons.search'),
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchValue).value = $event))
                  }, null, 8, _hoisted_3), [
                    [_vModelText, searchValue.value]
                  ]),
                  (searchValue.value !== '')
                    ? (_openBlock(), _createBlock(_unref(XMarkIcon), {
                        key: 0,
                        class: "w-4 absolute items-center bottom-0 right-1 top-1.5 text-gray-500",
                        onClick: resetSearchBar
                      }))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_4, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((filteredGroups.value as typeof groups), (group) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: group.field,
                      class: "relative",
                      id: group.field
                    }, [
                      _createElementVNode("div", _hoisted_6, [
                        _createElementVNode("p", _hoisted_7, _toDisplayString(group.name), 1),
                        (selected.value[group.field]?.size)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              onClick: _cache[1] || (_cache[1] = ($event: any) => (clearSelected())),
                              class: "text-gray-500 text-xs hover:underline"
                            }, _toDisplayString(_ctx.$t("buttons.clear")), 1))
                          : _createCommentVNode("", true)
                      ]),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((group.options as typeof options), (option) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: option.value
                        }, [
                          (option.name.toLowerCase().includes(searchValue.value.toLowerCase()))
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "relative cursor-pointer select-none py-2 pl-5 pr-9 text-left hover:bg-yellow-200 rounded",
                                onClick: ($event: any) => (handleGroupsChange(group.field, option.value))
                              }, [
                                _createElementVNode("div", _hoisted_9, [
                                  ((selected.value as Record<string, Set<string>>)[group.field]?.has?.(option.value) && __props.multiple && __props.dependencies[group.field] && !countDependencies(group.field, option.value).every((v, _, arr) => v === arr[0]))
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, " - "))
                                    : (_openBlock(), _createElementBlock("input", {
                                        key: 1,
                                        checked: selected.value[group.field]?.has(option.value),
                                        type: "checkbox",
                                        class: "h-4 w-4 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 cursor-pointer"
                                      }, null, 8, _hoisted_11)),
                                  ((selected.value as Record<string, Set<string>>)[group.field]?.has?.(option.value) && __props.multiple)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, [
                                        (__props.dependencies[group.field])
                                          ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(`${countDependencies(group.field, option.value).join("/")}`), 1))
                                          : _createCommentVNode("", true)
                                      ]))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("label", _hoisted_14, _toDisplayString(option.name), 1)
                                ])
                              ], 8, _hoisted_8))
                            : _createCommentVNode("", true)
                        ]))
                      }), 128))
                    ], 8, _hoisted_5))
                  }), 128))
                ])
              ], 512)
            ]),
            _: 1
          }, 8, ["static", "class"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})